import { useState, useEffect } from "react";
import {
  SelectShapes,
  SelectColors,
  SelectSizes,
  SelectStatus,
  SelectSizeGrouping,
} from "components/ui/selectors";
import Select from "components/ui/Select";
import Alert from "components/ui/Alert";
import {
  useOneAgitemdetail,
  useEditItemDetail,
  useCheckItemDetailUsage,
} from "http-hooks/agitemdetail.js/useAgitemdetail";
import useCompanyPreferences from "hooks/useCompanyPreferences";

// Constants moved to top to prevent re-renders
const sizeTypeOptions = [
  { value: "B", label: "Big" },
  { value: "S", label: "Small" },
];

// Add this loading skeleton component
const FormSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-7 bg-gray-200 rounded w-1/4 mb-8"></div>
    <div className="space-y-6">
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-9 bg-gray-200 rounded w-full"></div>
      </div>
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-9 bg-gray-200 rounded w-full"></div>
      </div>
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-9 bg-gray-200 rounded w-full"></div>
      </div>
    </div>
  </div>
);

export default function EditItemDetail({ id, onClose }) {
  // Get company preferences to conditionally show fields
  const { isShapeEnabled, isColorEnabled, isSizeEnabled } =
    useCompanyPreferences();

  // Combined state for selected values
  const [selected, setSelected] = useState({
    shape: null,
    color: null,
    size: null,
    sizeType: "B",
    status: null,
    sizeGrouping: null,
  });

  const { isLoading: isItemLoading, data: itemDetail } = useOneAgitemdetail(
    id,
    !!id
  );
  const { isLoading: isUsageLoading, data: usageData } =
    useCheckItemDetailUsage(id, !!id);

  const { mutate: mutateEdit, isPending: isUpdating } = useEditItemDetail(
    {
      onSuccess: () => {
        onClose();
      },
    }
  );

  const handleUpdate = () => {
    // Build shape object - use selected or create from current data
    const shape = selected.shape || {
      shapeId: 0, // Default if no shape selected
      shapetype: itemDetail.shp
    };

    // Build color object - use selected or create from current data  
    const color = selected.color || {
      colorId: 0, // Default if no color selected
      clrname: itemDetail.clt
    };

    // Build size object - use selected or create from current data
    const size = selected.size || {
      itmszid: 0, // Default if no size selected
      itmsz: itemDetail.sz,
      umid: itemDetail.szuom
    };

    const updateData = {
      itemId: id,
      shape: shape,
      color: color,
      size: size,
      size_type: selected.sizeType,
      status: selected.status?.value ?? 1,
      size_grouping_id: selected.sizeGrouping?.value ?? null,
    };

    mutateEdit(updateData);
  };

  useEffect(() => {
    if (itemDetail) {
      setSelected((prev) => ({
        ...prev,
        shape: itemDetail.shp ? { shapetype: itemDetail.shp } : null,
        color: itemDetail.clt ? { clrname: itemDetail.clt } : null,
        size: itemDetail.sz && itemDetail.szuom ? { itmsz: itemDetail.sz, umid: itemDetail.szuom } : null,
        sizeType: itemDetail.itmsztyp || "B",
        status: itemDetail.ag_status !== undefined ? { value: itemDetail.ag_status, label: itemDetail.ag_status === 1 ? "Active" : "Inactive" } : null,
        sizeGrouping: itemDetail.size_grouping_id && itemDetail.sizeGrouping
          ? {
              value: itemDetail.size_grouping_id,
              label: itemDetail.sizeGrouping.size_uom
                ? `${itemDetail.sizeGrouping.name} (${itemDetail.sizeGrouping.size_uom})`
                : itemDetail.sizeGrouping.name,
              ...itemDetail.sizeGrouping,
            }
          : null,
      }));
    }
  }, [itemDetail]);

  // Show loading skeleton while either data is loading
  if (id && (isItemLoading || isUsageLoading)) {
    return <FormSkeleton />;
  }

  // Only show form when both itemDetail and usageData are available
  if (!itemDetail || !usageData) {
    return <FormSkeleton />;
  }

  const isItemUsed = usageData.isUsed;
  const usedInTable = usageData.usedIn?.[0] || "";

  return (
    <form>
      <div className="space-y-8">
        <div className="border-b border-gray-900/10 pb-10">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Edit Item Detail
          </h2>

          {/* Usage Warning Message */}
          {isItemUsed && (
            <Alert
              variant="warning"
              title="Item Cannot Be Edited"
              className="mt-4"
            >
              <p>
                This item detail cannot be edited as it is already in use in{" "}
                <span className="font-semibold">{usedInTable}</span>, but the
                size type, status and size group can be changed.
              </p>
            </Alert>
          )}

          <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
            {isShapeEnabled() && (
              <div className="col-span-full">
                <SelectShapes
                  value={selected.shape}
                  onChange={(selectedOption) =>
                    setSelected((prev) => ({
                      ...prev,
                      shape: selectedOption,
                    }))
                  }
                  placeholder="Search shapes..."
                  label="Shape"
                  isDisabled={isItemUsed}
                />
              </div>
            )}

            {isColorEnabled() && (
              <div className="col-span-full">
                <SelectColors
                  value={selected.color}
                  onChange={(selectedOption) =>
                    setSelected((prev) => ({
                      ...prev,
                      color: selectedOption,
                    }))
                  }
                  placeholder="Search colors..."
                  label="Color"
                  isDisabled={isItemUsed}
                />
              </div>
            )}

            {isSizeEnabled() && (
              <div className="col-span-full">
                <SelectSizes
                  value={selected.size}
                  onChange={(selectedOption) =>
                    setSelected((prev) => ({
                      ...prev,
                      size: selectedOption,
                    }))
                  }
                  placeholder="Search sizes..."
                  label="Size"
                  isDisabled={isItemUsed}
                />
              </div>
            )}

            {isSizeEnabled() && (
              <div className="col-span-full">
                <Select
                  label="Size Type"
                  value={sizeTypeOptions.find(
                    (option) => option.value === selected.sizeType
                  )}
                  onChange={(selectedOption) =>
                    setSelected((prev) => ({
                      ...prev,
                      sizeType: selectedOption?.value || "B",
                    }))
                  }
                  options={sizeTypeOptions}
                  placeholder="Select size type"
                  isSearchable={false}
                  isClearable={false}
                  isDisabled={false} // Size type can always be changed
                />
              </div>
            )}

            {isSizeEnabled() && (
              <div className="col-span-full">
                <SelectSizeGrouping
                  label="Size Group"
                  value={selected.sizeGrouping}
                  onChange={(selectedOption) =>
                    setSelected((prev) => ({ ...prev, sizeGrouping: selectedOption }))
                  }
                  isClearable={true}
                />
              </div>
            )}

            <div className="col-span-full">
              <SelectStatus
                label="Status"
                value={selected.status}
                onChange={(selectedOption) =>
                  setSelected((prev) => ({
                    ...prev,
                    status: selectedOption,
                  }))
                }
                placeholder="Select status"
                isDisabled={false} // Status can always be changed
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button
          type="button"
          className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
          onClick={onClose}
          disabled={isUpdating}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={isUpdating}
          onClick={handleUpdate}
          className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUpdating ? "Updating..." : "Update"}
        </button>
      </div>
    </form>
  );
}
